import type { LanguageOption } from '@/i18n/languages';
import { ChoiceTile } from './choice-tile';

/**
 * The language picker: one tile per enabled language, its code (or a flag) and
 * native name, in `site_settings` order. Ticket 04's header sheet renders the
 * same list, which is why this takes the options rather than reading them
 * itself.
 *
 * Nothing here schedules the entrance: each tile inherits the stage's motion
 * label and the stage staggers them in render order.
 */
export function LanguageTiles({
  languages,
  selected,
  onSelect,
}: {
  languages: Array<LanguageOption>;
  selected: string | null;
  onSelect: (code: string) => void;
}) {
  return (
    /*
      Seven tiles are taller than the thumb zone on a small phone, so the list
      scrolls inside it rather than pushing the card's bottom edge — and the
      back affordance above it — off the screen. `overscroll-contain` keeps a
      flick at the end of the list from dragging the page behind it. Only on a
      phone: from `sm` up there is no thumb zone to stay inside.
    */
    <ul className="-mx-1 flex max-h-[54dvh] flex-col gap-2 overflow-y-auto overscroll-contain px-1 py-0.5 sm:max-h-none sm:overflow-visible">
      {languages.map((language) => (
        <li key={language.code}>
          <ChoiceTile
            selected={language.code === selected}
            onClick={() => onSelect(language.code)}
            label={language.nativeName}
            lang={language.code}
            leading={
              language.flagUrl ? (
                /* Fixed box + `object-cover`: a flag from Prismic has no
                   knowable ratio, and 6×9 is the aspect the set is drawn at. */
                <img
                  src={language.flagUrl}
                  alt=""
                  width={36}
                  height={24}
                  className="ring-surf-dark-blue/10 h-6 w-9 rounded-sm object-cover ring-1"
                />
              ) : (
                /* The code in the flag's 36 px box, so names still line up.
                   Hidden from assistive tech: the native name is the label. */
                <span
                  aria-hidden
                  translate="no"
                  className="text-surf-dark-blue bg-surf-blue/8 w-9 rounded-md py-0.5 text-center text-xs font-bold tracking-wide"
                >
                  {language.codeLabel}
                </span>
              )
            }
          />
        </li>
      ))}
    </ul>
  );
}
